import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import api from '../utils/api';
import pushNotifications from '../utils/pushNotifications';
import { resolveNotificationNavigateTarget } from '../utils/notificationNavigation';

const NotificationContext = createContext();

const notificationTypeMap = {
  general: 'newMessages',
  new_booking_request: 'bookingRequests',
  booking_confirmed: 'bookingRequests',
  booking_rejected: 'bookingRequests',
  booking_cancelled: 'bookingRequests',
  booking_cancelled_by_client: 'bookingRequests',
  booking_rescheduled: 'bookingRequests',
  booking_reschedule_request: 'bookingRequests',
  reschedule_confirmed_by_client: 'bookingRequests',
  reschedule_rejected_by_client: 'bookingRequests',
  booking_completed: 'bookingRequests',
  change_request: 'bookingRequests',
  change_request_approved: 'bookingRequests',
  change_request_rejected: 'bookingRequests',
  team_member_assigned: 'bookingRequests',
  form_submission: 'formSubmissions',
  lead_reminder: 'reminders',
  lead_reminder_due_soon: 'reminders',
  lead_reminder_overdue: 'reminders',
  lead_reminder_overdue_7d: 'reminders',
  lead_follow_up: 'reminders',
  platform_update: 'platformUpdates'
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushSubscribeError, setPushSubscribeError] = useState(null);

  const getSupplierBusinessId = (explicitBusinessId) => {
    if (explicitBusinessId) return explicitBusinessId;
    if (user?.role !== 'supplier') return null;
    return localStorage.getItem('currentBusinessId') || null;
  };

  // Fetch notifications
  const fetchNotifications = async (page = 1, limit = 20, unreadOnly = false, businessId = null) => {
    if (!user) return;
    
    try {
      setLoading(true);
      const scopedBusinessId = getSupplierBusinessId(businessId);
      const response = await api.get('/api/notifications', {
        params: {
          page,
          limit,
          unreadOnly,
          ...(scopedBusinessId ? { businessId: scopedBusinessId } : {})
        }
      });
      
      if (page === 1) {
        setNotifications(response.data.notifications);
      } else {
        setNotifications(prev => [...prev, ...response.data.notifications]);
      }
      
      setUnreadCount(response.data.unreadCount);
      return response.data;
    } catch (error) {
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count only
  const fetchUnreadCount = async (businessId = null) => {
    if (!user) return;
    
    try {
      const scopedBusinessId = getSupplierBusinessId(businessId);
      const response = await api.get('/api/notifications/unread-count', {
        params: scopedBusinessId ? { businessId: scopedBusinessId } : undefined
      });
      setUnreadCount(response.data.unreadCount);
    } catch (error) {}
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    const scopedBusinessId = getSupplierBusinessId();
    try {
      await api.put(`/api/notifications/${notificationId}/read`, {}, {
        params: scopedBusinessId ? { businessId: scopedBusinessId } : undefined
      });
      
      setNotifications(prev => 
        prev.map(notification => 
          String(notification._id) === String(notificationId)
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );

      // Source of truth comes from server to avoid stale badge counts.
      fetchUnreadCount(scopedBusinessId);
    } catch (error) {
      fetchUnreadCount(scopedBusinessId);
      fetchNotifications(1, 20, false, scopedBusinessId);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const scopedBusinessId = getSupplierBusinessId();
      await api.put('/api/notifications/read-all', {}, {
        params: scopedBusinessId ? { businessId: scopedBusinessId } : undefined
      });
      
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          isRead: true, 
          readAt: new Date() 
        }))
      );
      
      setUnreadCount(0);
    } catch (error) {}
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    try {
      const scopedBusinessId = getSupplierBusinessId();
      await api.delete('/api/notifications/delete-all', {
        params: scopedBusinessId ? { businessId: scopedBusinessId } : undefined
      });
      
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {}
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      const scopedBusinessId = getSupplierBusinessId();
      await api.delete(`/api/notifications/${notificationId}`, {
        params: scopedBusinessId ? { businessId: scopedBusinessId } : undefined
      });
      
      setNotifications(prev => 
        prev.filter(notification => notification._id !== notificationId)
      );
      
      // Check if the deleted notification was unread
      const deletedNotification = notifications.find(n => n._id === notificationId);
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {}
  };

  // Add new notification (for real-time updates)
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  };

  const getNotificationBusinessId = (notification) => {
    if (!notification) return null;
    if (notification.business?._id) return String(notification.business._id);
    if (notification.business) return String(notification.business);
    if (notification.data?.businessId) return String(notification.data.businessId);
    return null;
  };

  // Load notifications when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [user]);

  // Refetch when socket reconnects (in case events were missed during disconnect)
  const wasConnected = useRef(false);
  useEffect(() => {
    if (!socket || !user) return;
    if (isConnected && !wasConnected.current) {
      wasConnected.current = true;
      fetchNotifications();
    } else if (!isConnected) {
      wasConnected.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [socket, isConnected, user]);

  // Listen for socket notifications
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      if (data.notification) {
        const isSupplier = user?.role === 'supplier';
        const scopedBusinessId = getSupplierBusinessId();
        const notificationBusinessId = getNotificationBusinessId(data.notification);
        const shouldShowForCurrentBusiness = !isSupplier ||
          !scopedBusinessId ||
          !notificationBusinessId ||
          notificationBusinessId === String(scopedBusinessId);

        if (shouldShowForCurrentBusiness) {
          addNotification(data.notification);
        }

        const preferenceKey = notificationTypeMap[data.notification.type];
        const channelPref = preferenceKey
          ? user?.settings?.notificationTypes?.[preferenceKey]?.push
          : user?.settings?.notifications?.push;
        const pushEnabled = channelPref === undefined ? true : Boolean(channelPref);
        const isTabFocused = document.hasFocus() && document.visibilityState === 'visible';
        if (isSupplier && pushEnabled && !isTabFocused) {
          pushNotifications.showNotification(data.notification.title, {
            body: data.notification.message,
            tag: `${data.notification.type}-${data.notification._id}`,
            requireInteraction: false,
            data: {
              url:
                resolveNotificationNavigateTarget(data.notification, { isSupplier: true }) ||
                '/supplier/notifications'
            }
          });
        }
      }
      // Refresh unread count
      fetchUnreadCount();
    };

    socket.on('new-notification', handleNewNotification);

    return () => {
      socket.off('new-notification', handleNewNotification);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [socket, isConnected, user]);

  useEffect(() => {
    if (!user) {
      setPushSubscribeError(null);
      return;
    }
    const isSupplier = user.role === 'supplier';
    // Keep a browser subscription unless push is explicitly disabled globally.
    // Per-type preferences are enforced server-side when deciding what to send.
    const pushEnabled = user?.settings?.notifications?.push !== false;
    if (!isSupplier) {
      setPushSubscribeError(null);
      return;
    }

    pushNotifications.syncPreference(pushEnabled).then((result) => {
      setPushSubscribeError(result?.success ? null : result?.error || null);
    });
  }, [user]);

  const clearPushSubscribeError = () => setPushSubscribeError(null);

  // Poll for unread count every 30 seconds (fallback)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [user]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
    deleteNotification,
    addNotification,
    pushSubscribeError,
    clearPushSubscribeError
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
